import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { FormTextFieldSkeleton } from "components/Skeleton";
import { UpdateWasteStreamInput, WasteClassification } from "generated-graphql/graphql";
import { Suspense, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { prettyPrintWasteClassification } from "../prettyPrintWasteClassification";
import { ProvisionalWasteStreamChip } from "../ProvisionalWasteStreamChip";
import { useUpdateWasteStream, useWasteStream } from "./api";

interface EditWasteStreamDialogProps {
  open: boolean;
  onClose: () => void;
  wasteStreamId: string;
}
interface UpdateWasteForm {
  name: string;
  comment: string;
  classification: WasteClassification | null;
  formCode: string;
  sourceCode: string;
  willNotReport: boolean;
}

interface EditWasteStreamFormProps {
  wasteStreamId: string;
  onClose: () => void;
}

const EditWasteStreamForm = ({ wasteStreamId, onClose }: EditWasteStreamFormProps) => {
  const {
    handleSubmit: handleSubmitForm,
    formState,
    reset,
    control,
    watch,
  } = useForm<UpdateWasteForm>({
    defaultValues: {
      name: "",
      comment: "",
      classification: null,
      formCode: "",
      sourceCode: "",
      willNotReport: false,
    },
  });
  const alerts = useAlerts();
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Fetch current waste stream data - no loading state needed with suspense
  const { data: wasteStreamData } = useWasteStream(wasteStreamId);

  // Update mutation
  const [mutateUpdateWasteStream, { loading: updating }] = useUpdateWasteStream();

  // Initialize form with current data
  useEffect(() => {
    if (wasteStreamData?.wasteStream) {
      reset({
        name: wasteStreamData.wasteStream.name,
        comment: wasteStreamData.wasteStream.comment ?? "",
        classification: wasteStreamData.wasteStream.classification || null,
        formCode: wasteStreamData.wasteStream.formCode ?? "",
        sourceCode: wasteStreamData.wasteStream.sourceCode ?? "",
        willNotReport: wasteStreamData.wasteStream.willNotReport ?? false,
      });
    }
  }, [wasteStreamData, reset]);

  const performUpdate = async (data: UpdateWasteForm) => {
    // Only send the fields the user actually changed. The resolver branches on
    // formCode/sourceCode presence to fire the code cascade onto raws and
    // vendor lines, so a willNotReport-only save must not include those keys.
    const dirty = formState.dirtyFields;
    const input: UpdateWasteStreamInput = {
      id: wasteStreamId,
    };
    if (dirty.name) input.name = data.name.trim();
    // `FormTextField` turns a cleared input into `null`, so `?.trim()` alone
    // would yield `undefined` and the field would be stripped from the
    // mutation. `|| null` lets the resolver actually clear the value.
    if (dirty.comment) input.comment = data.comment?.trim() || null;
    if (dirty.classification) input.classification = data.classification;
    if (dirty.formCode) input.formCode = data.formCode?.trim() || null;
    if (dirty.sourceCode) input.sourceCode = data.sourceCode?.trim() || null;
    if (dirty.willNotReport) input.willNotReport = data.willNotReport;

    try {
      const result = await mutateUpdateWasteStream({
        update: (cache, { data: responseData }) => {
          if (responseData?.updateWasteStream) {
            // Write the same values we sent so the cache matches what the
            // server stored — refetchQueries will reconcile any drift.
            cache.modify({
              id: `WasteStream:${wasteStreamId}`,
              fields: {
                ...(dirty.name && { name: () => data.name.trim() }),
                ...(dirty.comment && {
                  comment: () => data.comment?.trim() || null,
                }),
                ...(dirty.classification && {
                  classification: () => data.classification ?? null,
                }),
                ...(dirty.formCode && {
                  formCode: () => data.formCode?.trim() || null,
                }),
                ...(dirty.sourceCode && {
                  sourceCode: () => data.sourceCode?.trim() || null,
                }),
                ...(dirty.willNotReport && { willNotReport: () => data.willNotReport }),
              },
            });
          }
        },
        variables: { input },
      });
      if (result.data?.updateWasteStream) {
        alerts.success("Waste stream updated");
        onClose();
      }
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }
    } catch (error) {
      if (error instanceof Error) {
        alerts.error("Failed to update waste stream: " + error.message);
      } else {
        alerts.error("Failed to update waste stream", "An unknown error occurred");
      }
    }
  };

  const onSubmit = async (data: UpdateWasteForm) => {
    // Check if classification has changed
    const originalClassification = wasteStreamData?.wasteStream?.classification || null;
    const classificationChanged = originalClassification !== data.classification;

    if (classificationChanged) {
      // Show confirmation dialog and break out
      setShowConfirmation(true);
      return;
    }

    // Proceed with update
    await performUpdate(data);
  };

  // Handler for when user confirms the classification change
  const handleConfirmSubmit = async () => {
    const submitter = handleSubmitForm(performUpdate);
    await submitter();
    setShowConfirmation(false);
  };

  const handleClose = () => {
    if (!updating) {
      setShowConfirmation(false);
      onClose();
    }
  };

  const handleCancelConfirmation = () => {
    handleClose();
  };

  return (
    <>
      <DialogContent>
        {wasteStreamData?.wasteStream?.isProvisional && (
          <Box sx={{ pt: 1 }}>
            <ProvisionalWasteStreamChip isProvisional />
          </Box>
        )}
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormTextField name="name" label="Name" fullWidth control={control} />
          <FormTextField
            name="formCode"
            label="Form Code"
            fullWidth
            control={control}
            helperText="Updates the stream-level form code. Cascades to vendor-sourced line items; eManifest-only line item data is preserved."
          />
          <FormTextField
            name="sourceCode"
            label="Source Code"
            fullWidth
            control={control}
            helperText="Updates the stream-level source code. Cascades to vendor-sourced line items; eManifest-only line item data is preserved."
          />
          <FormTextField name="comment" label="Note" fullWidth control={control} />
          <FormSelect
            name="classification"
            label="Classification"
            hideErrors
            selectItems={Object.values(WasteClassification).map((value) => ({
              value,
              display: prettyPrintWasteClassification(value),
            }))}
            control={control}
          />
          <Controller
            name="willNotReport"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={<Switch checked={!value} onChange={(e) => onChange(!e.target.checked)} />}
                label={value ? "Not Reportable" : "Reportable"}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updating}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmitForm(onSubmit)}
          variant="contained"
          disabled={updating || !formState.isDirty}
        >
          {updating ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={showConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmSubmit}
        header="Are you sure you want to edit this waste stream classification?"
        resources={[
          {
            resourceType: "Waste Stream Name",
            resourceName: watch("name"),
          },
          {
            resourceType: "Previous Classification",
            resourceName: prettyPrintWasteClassification(
              wasteStreamData?.wasteStream?.classification
            ),
          },
          {
            resourceType: "New Classification",
            resourceName: prettyPrintWasteClassification(watch("classification")),
          },
        ]}
        info="Changing this classification will affect all of the line items in this waste stream."
        confirmText="Yes, Save Edits"
        cancelText="Cancel"
        loading={updating}
      />
    </>
  );
};

export const EditWasteStreamDialog = ({
  open,
  onClose,
  wasteStreamId,
}: EditWasteStreamDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Waste Stream</DialogTitle>
      <Suspense
        fallback={
          <DialogContent>
            <Stack sx={{ pt: 1 }}>
              <FormTextFieldSkeleton />
              <FormTextFieldSkeleton />
              <FormTextFieldSkeleton />
              <FormTextFieldSkeleton />
              <FormTextFieldSkeleton />
            </Stack>
          </DialogContent>
        }
      >
        <EditWasteStreamForm wasteStreamId={wasteStreamId} onClose={onClose} />
      </Suspense>
    </Dialog>
  );
};
